/**
 * Workshop showcase for the messaging frame shapes (ChatFrame + SlackFrame).
 *
 * Renders both frames with realistic mock data plus edge cases: empty
 * thread, streaming cursor, and error state. Follow-up pill clicks are
 * captured locally and shown above the gallery — pills never execute.
 *
 * NOTE: route registration in workshop-routes.tsx and sync-manifest.yaml
 * is owned by the main agent; this file only exports the page component.
 */

import { useState } from "react";
import { ChatFrame } from "@/workshop/components/shapes/chat-frame/chat-frame";
import type { ChatFrameMessage } from "@/workshop/components/shapes/chat-frame/chat-frame";
import { SlackFrame } from "@/workshop/components/shapes/slack-frame/slack-frame";
import type { SlackFrameMessage } from "@/workshop/components/shapes/slack-frame/slack-frame";

const GROUP_THREAD: ChatFrameMessage[] = [
  {
    id: "m1",
    from: "Mara",
    mine: false,
    text: "Dolcetto beta is live for 200 users. Crash-free rate is 99.8% — well above our 99.5% bar.",
    time: "18:02",
  },
  {
    id: "m2",
    from: "Jonas",
    mine: false,
    text: "Android vitals look clean too. The only spike is a slow-frame warning on the gallery screen for low-end devices.",
    time: "18:04",
  },
  {
    id: "m3",
    mine: true,
    text: "Great numbers. Let's fix the gallery jank before we ramp to 50% — can someone own it?",
    time: "18:05",
    read: true,
  },
  {
    id: "m4",
    from: "Priya",
    mine: false,
    text: "I'll take it. Profiling shows the blur pass running per-item instead of once per frame — should be a quick win. This is a longer message to show how multi-line bubbles wrap inside the frame without breaking the layout or pushing the follow-up chips off screen.",
    time: "18:07",
  },
];

const STREAMING_THREAD: ChatFrameMessage[] = [
  {
    id: "m1",
    from: "Mara",
    mine: false,
    text: "Can you review the release notes draft before I send it?",
    time: "18:10",
  },
  {
    id: "m2",
    mine: true,
    text: "On it — the highlights section needs one more line about the",
    time: "18:11",
    read: false,
  },
];

const CHANNEL_MESSAGES: SlackFrameMessage[] = [
  {
    id: "k1",
    user: "Mara Jensen",
    initials: "MJ",
    color: "#7c5cf0",
    time: "18:02",
    text: "Beta is live for 200 users — *crash-free 99.8%*. Dashboard: <https://example.com/vitals|release vitals>.",
    threadReplies: 5,
  },
  {
    id: "k2",
    user: "Jonas Weber",
    initials: "JW",
    color: "#2fa98c",
    time: "18:04",
    text: "Only flag is a *slow-frame warning* on the gallery screen for low-end devices.",
  },
  {
    id: "k3",
    user: "Priya Nair",
    initials: "PN",
    color: "#e07a5f",
    time: "18:07",
    text: "Owning the gallery fix — blur pass runs per-item instead of once per frame. Also testing *no-link plain text* and a bare <https://example.com/notes> URL render.",
    threadReplies: 1,
  },
];

const STREAMING_CHANNEL: SlackFrameMessage[] = [
  {
    id: "k1",
    user: "Mara Jensen",
    initials: "MJ",
    color: "#7c5cf0",
    time: "18:12",
    text: "Drafting the *ramp-to-50%* announcement now — polling the latest vitals",
  },
];

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="mb-10"
      data-testid={`messaging-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap items-start gap-6">{children}</div>
    </div>
  );
}

export function MessagingFramesPage() {
  const [lastFollowUp, setLastFollowUp] = useState<string | null>(null);

  return (
    <div className="workshop-canvas-bg min-h-full p-8" data-testid="messaging-frames-page">
      <style>{`.workshop-canvas-bg{background-color:#0f1115;background-image:linear-gradient(0deg,transparent,transparent),linear-gradient(135deg,#2b2f36 0%,#343a42 40%,#3d424a 70%,#3b2f2a 100%);position:relative}.workshop-canvas-bg::before{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;opacity:0.1;background:radial-gradient(circle at 22% 18%,rgba(156,132,255,0.18),transparent 58%),radial-gradient(circle at 72% 70%,rgba(111,168,198,0.12),transparent 60%)}.workshop-canvas-bg>*{position:relative;z-index:1}`}</style>
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Messaging Frames</h1>
        <p className="mt-1 text-sm text-white/40">
          WhatsApp-style thread and Slack channel shapes. Clicking a link
          or thread row in a Slack card captures the prompt
          {lastFollowUp ? (
            <>
              {" "}— last captured:{" "}
              <strong className="text-white/60">{lastFollowUp}</strong>
            </>
          ) : (
            " below."
          )}
        </p>
      </div>

      <Section title="Chat Frame">
        <div className="w-[380px]">
          <ChatFrame
            messages={GROUP_THREAD}
            status="done"
            data-testid="chat-frame-full"
          />
        </div>
        <div className="w-[380px]">
          <ChatFrame
            messages={[]}
            status="idle"
            data-testid="chat-frame-empty"
          />
        </div>
      </Section>

      <Section title="Chat Edge Cases">
        <div className="w-[380px]">
          <ChatFrame
            messages={STREAMING_THREAD}
            status="streaming"
            data-testid="chat-frame-streaming"
          />
        </div>
        <div className="w-[380px]">
          <ChatFrame
            messages={GROUP_THREAD}
            status="error"
            data-testid="chat-frame-error"
          />
        </div>
        <div className="w-[380px]">
          <ChatFrame
            messages={[]}
            status="streaming"
            data-testid="chat-frame-loading"
          />
        </div>
      </Section>

      <Section title="Slack Frame">
        <div className="w-[420px]">
          <SlackFrame
            channel="release"
            messages={CHANNEL_MESSAGES}
            status="done"
            onFollowUp={setLastFollowUp}
            data-testid="slack-frame-full"
          />
        </div>
        <div className="w-[420px]">
          <SlackFrame
            channel="empty-channel"
            messages={[]}
            status="idle"
            onFollowUp={setLastFollowUp}
            data-testid="slack-frame-empty"
          />
        </div>
      </Section>

      <Section title="Slack Edge Cases">
        <div className="w-[420px]">
          <SlackFrame
            channel="release"
            messages={STREAMING_CHANNEL}
            status="streaming"
            onFollowUp={setLastFollowUp}
            data-testid="slack-frame-streaming"
          />
        </div>
        <div className="w-[420px]">
          <SlackFrame
            channel="release"
            messages={CHANNEL_MESSAGES}
            status="error"
            onFollowUp={setLastFollowUp}
            data-testid="slack-frame-error"
          />
        </div>
      </Section>
    </div>
  );
}
